import React from 'react';
import { Info, AlertTriangle, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { IconButton } from './IconButton.js';

export type NoticeTone = 'info' | 'warning' | 'error' | 'success' | 'neutral';

export interface NoticeProps {
  tone?: NoticeTone | 'warn';
  icon?: React.ReactNode;
  title?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  onDismiss?: () => void;
  dismissTestId?: string;
  sticky?: boolean;
  role?: 'status' | 'alert';
  className?: string;
  'data-testid'?: string;
  testId?: string;
}

export const Notice: React.FC<NoticeProps> = ({
  tone = 'info',
  icon,
  title,
  children,
  action,
  onDismiss,
  dismissTestId,
  sticky = false,
  role = 'status',
  className = '',
  'data-testid': dataTestId,
  testId
}) => {
  const normalizedTone: NoticeTone = tone === 'warn' ? 'warning' : tone;

  const toneClasses: Record<NoticeTone, string> = {
    info: 'bg-info/12 text-info-soft',
    warning: 'bg-warning/12 text-warning-soft',
    error: 'bg-error/12 text-error-soft',
    success: 'bg-success/12 text-success-soft',
    neutral: 'bg-neutral border border-base-300 text-base-content/80'
  };

  const defaultIcons: Record<NoticeTone, React.ReactNode> = {
    info: <Info className="size-4 shrink-0 mt-1" aria-hidden="true" />,
    warning: <AlertTriangle className="size-4 shrink-0 mt-1" aria-hidden="true" />,
    error: <AlertCircle className="size-4 shrink-0 mt-1" aria-hidden="true" />,
    success: <CheckCircle2 className="size-4 shrink-0 mt-1" aria-hidden="true" />,
    neutral: <Info className="size-4 shrink-0 mt-1" aria-hidden="true" />
  };

  const renderedIcon = icon !== undefined ? (
    icon ? (
      <span className="size-4 shrink-0 mt-1 flex items-center justify-center" aria-hidden="true">
        {icon}
      </span>
    ) : null
  ) : (
    defaultIcons[normalizedTone]
  );

  const testIdValue = dataTestId || testId;

  const content = (
    <div
      role={role}
      data-testid={testIdValue}
      className={`flex items-start gap-2.5 rounded-xl px-3 py-2.5 text-xs leading-6 animate-fade-in motion-reduce:animate-none ${toneClasses[normalizedTone]} ${className}`}
    >
      {renderedIcon}
      <div className="flex-1 min-w-0">
        {title && <div className="font-semibold text-[13px] mb-0.5">{title}</div>}
        <div className="text-xs leading-5 break-words">{children}</div>
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
      {onDismiss && (
        <IconButton
          label="بستن"
          icon={<X className="size-4" />}
          variant="ghost"
          size="sm"
          data-testid={dismissTestId ?? (testIdValue ? `${testIdValue}-close` : undefined)}
          onClick={onDismiss}
          className="shrink-0 -me-1 -mt-1"
        />
      )}
    </div>
  );

  if (sticky) {
    return (
      <div className="sticky top-0 z-[5] shadow-sm backdrop-blur-sm bg-base-200 rounded-xl">
        {content}
      </div>
    );
  }

  return content;
};

Notice.displayName = 'Notice';
